import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Ship, Search } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = ["In Transit", "At Port", "Customs Clearance", "Delivered"];

const STATUS_STYLES = {
  "In Transit": "bg-blue-500/20 text-blue-300",
  "At Port": "bg-gold/20 text-gold",
  "Customs Clearance": "bg-amber-500/20 text-amber-300",
  Delivered: "bg-emerald-500/20 text-emerald-300",
};

const EMPTY_FORM = {
  reference_number: "",
  reference_type: "FQ",
  container_number: "",
  carrier_name: "",
  origin_port: "",
  destination_port: "",
  eta: "",
  status: "In Transit",
  notes: "",
};

export const ShipmentManager = () => {
  const [shipments, setShipments] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchShipments = async () => {
    try {
      const { data } = await apiClient.get("/admin/shipments");
      setShipments(data);
    } catch {
      toast.error("Could not load shipments");
    }
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  const openAddForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (s) => {
    setEditingId(s.id);
    setForm({
      reference_number: s.reference_number || "",
      reference_type: "FQ",
      container_number: s.container_number || "",
      carrier_name: s.carrier_name || "",
      origin_port: s.origin_port || "",
      destination_port: s.destination_port || "",
      eta: s.eta || "",
      status: s.status || "In Transit",
      notes: s.notes || "",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.origin_port.trim() || !form.destination_port.trim()) {
      toast.error("Origin Port and Destination Port are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiClient.patch(`/admin/shipments/${editingId}`, {
          container_number: form.container_number.trim() || null,
          carrier_name: form.carrier_name.trim() || null,
          origin_port: form.origin_port.trim(),
          destination_port: form.destination_port.trim(),
          eta: form.eta.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null,
        });
        toast.success("Shipment updated");
      } else {
        const { data } = await apiClient.post("/admin/shipments", {
          reference_number: form.reference_number.trim() || null,
          reference_type: form.reference_type,
          container_number: form.container_number.trim() || null,
          carrier_name: form.carrier_name.trim() || null,
          origin_port: form.origin_port.trim(),
          destination_port: form.destination_port.trim(),
          eta: form.eta.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null,
        });
        toast.success(`Shipment added — reference ${data.reference_number}`);
      }
      closeForm();
      fetchShipments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save shipment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this shipment record? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/admin/shipments/${id}`);
      toast.success("Shipment deleted");
      fetchShipments();
    } catch {
      toast.error("Could not delete shipment");
    }
  };

  const filteredShipments = shipments
    ? shipments
        .filter((s) => statusFilter === "all" || s.status === statusFilter)
        .filter((s) => {
          const q = searchQuery.trim().toUpperCase();
          if (!q) return true;
          return (
            s.reference_number?.toUpperCase().includes(q) ||
            s.container_number?.toUpperCase().includes(q)
          );
        })
    : shipments;

  return (
    <div data-testid="admin-shipment-manager">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <p className="text-sm text-[#A1B0A6] max-w-md">
          Manually enter existing shipments here so your team and clients can look them up by
          reference number or container number from the public "Track Your Shipment" dialog.
        </p>
        <button
          onClick={openAddForm}
          data-testid="admin-shipment-add-btn"
          className="flex items-center gap-1.5 text-sm font-medium bg-gold hover:bg-gold-hover text-[#0A140F] rounded-full px-4 py-2 transition-colors shrink-0"
        >
          <Plus size={14} /> Add Shipment
        </button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7E74]" />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by reference or container number"
          data-testid="admin-shipment-search-input"
          className="admin-field pl-9 w-full"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            data-testid="admin-shipment-search-clear-btn"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7E74] hover:text-gold"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="flex gap-1.5 mb-5 flex-wrap">
        {["all", ...STATUS_OPTIONS].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`admin-shipment-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s ? "bg-gold text-[#0A140F]" : "bg-deep-card text-[#A1B0A6] hover:text-gold"
            }`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {formOpen && (
        <div
          data-testid="admin-shipment-form"
          className="rounded-xl border border-gold/15 bg-deep-card p-4 sm:p-5 mb-6 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="font-display font-semibold text-[#F4F7F5]">
              {editingId ? "Edit Shipment" : "New Shipment"}
            </span>
            <button onClick={closeForm} data-testid="admin-shipment-cancel-btn" className="text-[#A1B0A6] hover:text-gold">
              <X size={16} />
            </button>
          </div>

          {!editingId && (
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                value={form.reference_number}
                onChange={(e) => setField("reference_number", e.target.value.toUpperCase())}
                placeholder="Reference number (leave blank to auto-generate)"
                data-testid="admin-shipment-input-reference"
                className="admin-field"
              />
              <Select value={form.reference_type} onValueChange={(v) => setField("reference_type", v)}>
                <SelectTrigger data-testid="admin-shipment-select-reference-type" className="admin-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FQ">Auto-generate as FQ- (Freight Quote style)</SelectItem>
                  <SelectItem value="CF">Auto-generate as CF- (Customs Filing style)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <input
              value={form.container_number}
              onChange={(e) => setField("container_number", e.target.value.toUpperCase())}
              placeholder="Container number (e.g. MSKU1234567)"
              data-testid="admin-shipment-input-container"
              className="admin-field"
            />
            <input
              value={form.carrier_name}
              onChange={(e) => setField("carrier_name", e.target.value)}
              placeholder="Carrier name (e.g. Maersk)"
              data-testid="admin-shipment-input-carrier"
              className="admin-field"
            />
            <input
              value={form.origin_port}
              onChange={(e) => setField("origin_port", e.target.value)}
              placeholder="Origin port *"
              data-testid="admin-shipment-input-origin"
              className="admin-field"
            />
            <input
              value={form.destination_port}
              onChange={(e) => setField("destination_port", e.target.value)}
              placeholder="Destination port *"
              data-testid="admin-shipment-input-destination"
              className="admin-field"
            />
            <input
              type="date"
              value={form.eta}
              onChange={(e) => setField("eta", e.target.value)}
              data-testid="admin-shipment-input-eta"
              className="admin-field"
            />
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger data-testid="admin-shipment-select-status" className="admin-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Internal notes (optional)"
            rows={2}
            data-testid="admin-shipment-textarea-notes"
            className="bg-deep border-gold/15 text-[#F4F7F5] placeholder:text-[#6E7E74]"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-shipment-save-btn"
            className="bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] text-sm font-display font-semibold px-5 py-2 rounded-md transition-colors"
          >
            {saving ? "Saving..." : editingId ? "Save Changes" : "Add Shipment"}
          </button>
        </div>
      )}

      {!shipments ? (
        <p className="text-[#A1B0A6] text-sm">Loading shipments...</p>
      ) : filteredShipments.length === 0 ? (
        <p className="text-[#A1B0A6] text-sm" data-testid="admin-shipment-empty">
          {searchQuery.trim()
            ? `No shipments match "${searchQuery.trim()}".`
            : statusFilter === "all"
            ? "No shipments entered yet."
            : `No shipments with status "${statusFilter}".`}
        </p>
      ) : (
        <div className="space-y-3">
          {filteredShipments.map((s) => (
            <div
              key={s.id}
              data-testid={`admin-shipment-row-${s.id}`}
              className="rounded-xl border border-gold/15 bg-deep-card p-4 sm:p-5 flex items-start justify-between gap-4 flex-wrap"
            >
              <div className="min-w-0 flex items-start gap-3">
                <Ship size={18} className="text-gold shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[#F4F7F5]">{s.reference_number}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status]}`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="text-sm text-[#D7E0DA] mt-1">
                    {s.origin_port} → {s.destination_port}
                  </div>
                  <div className="text-xs text-[#6E7E74] mt-1">
                    {s.carrier_name || "Carrier not specified"}
                    {s.container_number && <> &bull; Container {s.container_number}</>}
                    {s.eta && <> &bull; ETA {s.eta}</>}
                  </div>
                  {s.notes && <div className="text-xs text-[#6E7E74] mt-1">{s.notes}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => openEditForm(s)}
                  data-testid={`admin-shipment-edit-btn-${s.id}`}
                  className="text-[#A1B0A6] hover:text-gold p-1.5"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => handleDelete(s.id)}
                  data-testid={`admin-shipment-delete-btn-${s.id}`}
                  className="text-[#A1B0A6] hover:text-red-400 p-1.5"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
